import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Plus, Edit, Trash2, User, Car, Phone, Mail, Calendar, AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { apiClient } from '../../utils/apiHelper';
import { formatDate } from '../../utils/dateTimeUtils';

const DriverSetting = () => {
  const [drivers, setDrivers] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDriver, setEditingDriver] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    driver_name: '',
    employee_id: '',
    phone: '',
    email: '',
    license_number: '',
    license_expiry: '',
    address: '',
    joining_date: new Date().toISOString().split('T')[0],
    vehicle_id: '',
    vehicle_number: '',
    status: 'Active'
  });

  useEffect(() => {
    fetchDrivers();
    fetchStaff();
    fetchVehicles();
  }, []);

  const fetchDrivers = async () => {
    try {
      const response = await apiClient.get('/api/transport/drivers');
      setDrivers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching drivers:', error);
    }
  };

  const fetchStaff = async () => {
    try {
      const response = await apiClient.get('/api/staff/');
      setStaffList(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching staff:', error);
    }
  };

  const fetchVehicles = async () => {
    try {
      const response = await apiClient.get('/api/transport/vehicles');
      setVehicles(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    }
  };

  const handleStaffChange = (staffId) => {
    const staff = staffList.find(s => s.id === staffId);
    if (staff) {
      setFormData({
        ...formData,
        driver_name: staff.full_name || staff.name || '',
        employee_id: staff.employee_id || staff.id,
        phone: staff.phone || staff.mobile || '',
        email: staff.email || ''
      });
    }
  };

  const handleVehicleChange = (vehicleId) => {
    const vehicle = vehicles.find(v => v.id === vehicleId);
    if (vehicle) {
      setFormData({
        ...formData,
        vehicle_id: vehicleId,
        vehicle_number: vehicle.vehicle_number
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingDriver) {
        await apiClient.put(`/api/transport/drivers/${editingDriver.id}`, formData);
      } else {
        await apiClient.post('/api/transport/drivers', formData);
      }
      alert('Driver saved successfully!');
      fetchDrivers();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving driver:', error);
      alert('Failed to save driver');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this driver?')) {
      try {
        await apiClient.delete(`/api/transport/drivers/${id}`);
        fetchDrivers();
      } catch (error) {
        alert('Failed to delete driver');
      }
    }
  };

  const handleEdit = (driver) => {
    setEditingDriver(driver);
    setFormData({
      driver_name: driver.driver_name || '',
      employee_id: driver.employee_id || '',
      phone: driver.phone || '',
      email: driver.email || '',
      license_number: driver.license_number || '',
      license_expiry: driver.license_expiry || '',
      address: driver.address || '',
      joining_date: driver.joining_date || new Date().toISOString().split('T')[0],
      vehicle_id: driver.vehicle_id || '',
      vehicle_number: driver.vehicle_number || '',
      status: driver.status || 'Active'
    });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingDriver(null);
    setFormData({
      driver_name: '',
      employee_id: '',
      phone: '',
      email: '',
      license_number: '',
      license_expiry: '',
      address: '',
      joining_date: new Date().toISOString().split('T')[0],
      vehicle_id: '',
      vehicle_number: '',
      status: 'Active'
    });
  };

  // Check if license is expiring soon (within 30 days) or expired
  const getLicenseStatus = (expiryDate) => {
    if (!expiryDate) return 'unknown';
    const expiry = new Date(expiryDate);
    const today = new Date();
    const daysUntilExpiry = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    
    if (daysUntilExpiry < 0) return 'expired';
    if (daysUntilExpiry <= 30) return 'expiring';
    return 'valid';
  };

  // Summary stats
  const activeDrivers = drivers.filter(d => d.status === 'Active').length;
  const expiringLicenses = drivers.filter(d => getLicenseStatus(d.license_expiry) === 'expiring').length;
  const expiredLicenses = drivers.filter(d => getLicenseStatus(d.license_expiry) === 'expired').length;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Car className="w-6 h-6 text-blue-600" />
            Driver Management
          </h1>
          <p className="text-gray-500 text-sm">Manage transport drivers and their assignments</p>
        </div>
        <Button onClick={() => setIsModalOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Add Driver
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <p className="text-sm text-blue-600">Total Drivers</p>
            <p className="text-2xl font-bold text-blue-700">{drivers.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4">
            <p className="text-sm text-green-600">Active</p>
            <p className="text-2xl font-bold text-green-700">{activeDrivers}</p>
          </CardContent>
        </Card>
        <Card className={`${expiringLicenses > 0 ? 'bg-yellow-50 border-yellow-200' : 'bg-gray-50 border-gray-200'}`}>
          <CardContent className="p-4">
            <p className={`text-sm ${expiringLicenses > 0 ? 'text-yellow-600' : 'text-gray-500'}`}>License Expiring</p>
            <p className={`text-2xl font-bold ${expiringLicenses > 0 ? 'text-yellow-700' : 'text-gray-600'}`}>{expiringLicenses}</p>
          </CardContent>
        </Card>
        <Card className={`${expiredLicenses > 0 ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}>
          <CardContent className="p-4">
            <p className={`text-sm ${expiredLicenses > 0 ? 'text-red-600' : 'text-gray-500'}`}>License Expired</p>
            <p className={`text-2xl font-bold ${expiredLicenses > 0 ? 'text-red-700' : 'text-gray-600'}`}>{expiredLicenses}</p>
          </CardContent>
        </Card>
      </div>

      {/* Drivers Table */}
      <Card>
        <CardContent className="pt-4">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="text-left p-3">Driver</th>
                <th className="text-left p-3">Employee ID</th>
                <th className="text-left p-3">Contact</th>
                <th className="text-left p-3">License #</th>
                <th className="text-left p-3">License Expiry</th>
                <th className="text-left p-3">Vehicle</th>
                <th className="text-center p-3">Status</th>
                <th className="text-center p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {drivers.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center p-8 text-gray-500">
                    <Car className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p>No drivers found.</p>
                    <p className="text-sm">Click "Add Driver" to add a new driver.</p>
                  </td>
                </tr>
              ) : (
                drivers.map((driver) => {
                  const licenseStatus = getLicenseStatus(driver.license_expiry);
                  return (
                    <tr key={driver.id} className={`border-b hover:bg-gray-50 ${licenseStatus === 'expired' ? 'bg-red-50' : licenseStatus === 'expiring' ? 'bg-yellow-50' : ''}`}>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                            <User className="w-4 h-4 text-blue-600" />
                          </div>
                          <span className="font-medium">{driver.driver_name}</span>
                        </div>
                      </td>
                      <td className="p-3">
                        <span className="px-2 py-1 text-xs rounded bg-gray-100">{driver.employee_id}</span>
                      </td>
                      <td className="p-3">
                        <div className="text-sm">
                          <div className="flex items-center gap-1">
                            <Phone className="w-3 h-3 text-gray-400" />
                            {driver.phone}
                          </div>
                          {driver.email && (
                            <div className="flex items-center gap-1 text-gray-500">
                              <Mail className="w-3 h-3 text-gray-400" />
                              {driver.email}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="p-3 font-mono">{driver.license_number}</td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          {licenseStatus === 'expired' && <AlertTriangle className="w-4 h-4 text-red-500" />}
                          {licenseStatus === 'expiring' && <AlertTriangle className="w-4 h-4 text-yellow-500" />}
                          <span className={`${licenseStatus === 'expired' ? 'text-red-600' : licenseStatus === 'expiring' ? 'text-yellow-600' : ''}`}>
                            {driver.license_expiry ? formatDate(driver.license_expiry) : '-'}
                          </span>
                        </div>
                      </td>
                      <td className="p-3">{driver.vehicle_number || '-'}</td>
                      <td className="p-3 text-center">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          driver.status === 'Active' ? 'bg-green-100 text-green-800' : 
                          driver.status === 'On Leave' ? 'bg-yellow-100 text-yellow-800' : 
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {driver.status}
                        </span>
                      </td>
                      <td className="p-3">
                        <div className="flex gap-2 justify-center">
                          <Button size="sm" variant="outline" onClick={() => handleEdit(driver)}>
                            <Edit className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => handleDelete(driver.id)}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Add/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingDriver ? 'Edit' : 'Add'} Driver</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Staff Selection (Optional - for linking to HR) */}
            {!editingDriver && staffList.length > 0 && (
              <div>
                <Label>Link to Staff (Optional)</Label>
                <Select onValueChange={handleStaffChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="-- Select from Staff --" />
                  </SelectTrigger>
                  <SelectContent>
                    {staffList.map((staff) => (
                      <SelectItem key={staff.id} value={staff.id}>
                        {staff.full_name || staff.name} ({staff.employee_id})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">Auto-fills details from staff record</p>
              </div>
            )}

            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Driver Name *</Label>
                <Input 
                  required 
                  value={formData.driver_name} 
                  onChange={(e) => setFormData({ ...formData, driver_name: e.target.value })} 
                />
              </div>
              <div>
                <Label>Employee ID *</Label>
                <Input 
                  required 
                  value={formData.employee_id} 
                  onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })} 
                />
              </div>
            </div>

            {/* Contact */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Phone *</Label>
                <Input 
                  required 
                  value={formData.phone} 
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })} 
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input 
                  type="email" 
                  value={formData.email} 
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })} 
                />
              </div>
            </div>

            {/* License Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>License Number *</Label>
                <Input 
                  required 
                  value={formData.license_number} 
                  onChange={(e) => setFormData({ ...formData, license_number: e.target.value })} 
                />
              </div>
              <div>
                <Label>License Expiry *</Label>
                <Input 
                  type="date" 
                  required 
                  value={formData.license_expiry} 
                  onChange={(e) => setFormData({ ...formData, license_expiry: e.target.value })} 
                />
              </div>
            </div>

            {/* Vehicle Assignment */}
            <div>
              <Label>Assign Vehicle (Optional)</Label>
              <Select value={formData.vehicle_id} onValueChange={(value) => {
                if (value === 'none') {
                  setFormData({ ...formData, vehicle_id: '', vehicle_number: '' });
                } else {
                  handleVehicleChange(value);
                }
              }}>
                <SelectTrigger>
                  <SelectValue placeholder="-- No Vehicle (Add Later) --" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- No Vehicle --</SelectItem>
                  {vehicles.map((v) => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.vehicle_number} - {v.vehicle_type_name || v.vehicle_type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">💡 You can add driver first, then assign vehicle later</p>
            </div>

            {/* Dates and Status */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Joining Date *</Label>
                <Input 
                  type="date" 
                  required 
                  value={formData.joining_date} 
                  onChange={(e) => setFormData({ ...formData, joining_date: e.target.value })} 
                />
              </div>
              <div>
                <Label>Status *</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="On Leave">On Leave</SelectItem>
                    <SelectItem value="Inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Address */}
            <div>
              <Label>Address</Label>
              <Textarea 
                value={formData.address} 
                onChange={(e) => setFormData({ ...formData, address: e.target.value })} 
                rows={2}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseModal}>Cancel</Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Saving...' : 'Save Driver'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DriverSetting;
